export type ThemeMode = "light" | "dark" | "system";

export const THEME_PALETTE_IDS = [
  "editorial_intelligence",
  "material_you_dynamic",
  "premium_amoled",
  "glassmorphism",
  "minimal_white_luxury",
  "cyberpunk_neon",
  "nature",
  "midnight_blue_pro",
  "sunset_gradient",
  "accessibility_high_contrast",
  "minimal_monochrome",
  "festival_burst",
] as const;

export type ThemePaletteId = (typeof THEME_PALETTE_IDS)[number];

export const DEFAULT_THEME_PALETTE_ID: ThemePaletteId = "editorial_intelligence";
export const EARNED_THEME_PALETTE_IDS = ["festival_burst"] as const;
export const CURATED_THEME_PALETTE_IDS = [
  "editorial_intelligence",
  "material_you_dynamic",
  "premium_amoled",
  "nature",
  "midnight_blue_pro",
  "sunset_gradient",
  "minimal_monochrome",
] as const satisfies readonly ThemePaletteId[];

export type ThemePaletteCategory =
  | "signature"
  | "dynamic"
  | "dark_premium"
  | "light_premium"
  | "expressive"
  | "accessibility";

type MaterialPalette = {
  primary: string;
  onPrimary: string;
  primaryContainer: string;
  onPrimaryContainer: string;
  secondary: string;
  onSecondary: string;
  tertiary?: string;
  background: string;
  onBackground: string;
  surface: string;
  onSurface: string;
  surfaceVariant: string;
  onSurfaceVariant: string;
  outline: string;
  outlineVariant: string;
  error?: string;
  onError?: string;
  success?: string;
};

export type ThemeCssVarName =
  | "--tb-bg-canvas"
  | "--tb-bg-elevated"
  | "--tb-bg-subtle"
  | "--tb-bg-soft"
  | "--tb-fg-primary"
  | "--tb-fg-secondary"
  | "--tb-fg-muted"
  | "--tb-fg-faint"
  | "--tb-accent"
  | "--tb-accent-hover"
  | "--tb-accent-contrast"
  | "--tb-tertiary"
  | "--tb-premium"
  | "--tb-border"
  | "--tb-border-strong"
  | "--tb-danger"
  | "--tb-danger-contrast"
  | "--tb-success"
  | "--tb-accent-soft";

export type ThemeTokenSet = {
  cssVars: Record<ThemeCssVarName, string>;
  metaThemeColor: string;
  swatches: string[];
};

export type ThemePalette = {
  id: ThemePaletteId;
  label: string;
  description: string;
  category: ThemePaletteCategory;
  premium: boolean;
  light: ThemeTokenSet;
  dark: ThemeTokenSet;
};

export const LEGACY_WEB_THEME_TO_PALETTE: Record<string, ThemePaletteId> = {
  light: "minimal_white_luxury",
  midnight: "premium_amoled",
  nord: "midnight_blue_pro",
  solarized: "sunset_gradient",
  dark: "premium_amoled",
};

const PALETTE_META: Record<
  ThemePaletteId,
  {
    label: string;
    description: string;
    category: ThemePaletteCategory;
    premium: boolean;
  }
> = {
  editorial_intelligence: {
    label: "Editorial",
    description: "Warm paper and deep ink.",
    category: "signature",
    premium: false,
  },
  material_you_dynamic: {
    label: "Material You",
    description: "Wallpaper-aware accents on supported devices.",
    category: "dynamic",
    premium: false,
  },
  premium_amoled: {
    label: "Premium AMOLED",
    description: "Pure blacks with refined neon accents.",
    category: "dark_premium",
    premium: true,
  },
  glassmorphism: {
    label: "Glass",
    description: "Frosted surfaces with airy layering.",
    category: "expressive",
    premium: true,
  },
  minimal_white_luxury: {
    label: "Minimal luxury",
    description: "Clean whites with soft elevation.",
    category: "light_premium",
    premium: false,
  },
  cyberpunk_neon: {
    label: "Cyberpunk neon",
    description: "Futuristic dark UI with electric accents.",
    category: "dark_premium",
    premium: true,
  },
  nature: {
    label: "Nature",
    description: "Earthy greens and calm warmth.",
    category: "expressive",
    premium: true,
  },
  midnight_blue_pro: {
    label: "Midnight pro",
    description: "Deep blues for a polished dashboard feel.",
    category: "dark_premium",
    premium: true,
  },
  sunset_gradient: {
    label: "Sunset",
    description: "Warm gradients without harsh saturation.",
    category: "expressive",
    premium: true,
  },
  accessibility_high_contrast: {
    label: "High contrast",
    description: "Maximum readability.",
    category: "accessibility",
    premium: false,
  },
  minimal_monochrome: {
    label: "Ultra minimal",
    description: "Refined grayscale typography-first UI.",
    category: "light_premium",
    premium: true,
  },
  festival_burst: {
    label: "Festival",
    description: "Gold and crimson, earned at 30 days.",
    category: "expressive",
    premium: false,
  },
};

const MATERIAL_PALETTES: Record<ThemePaletteId, { light: MaterialPalette; dark: MaterialPalette }> =
  {
    // The default shares Android's EditionColorSchemes. Browser contrast guards
    // below may strengthen a foreground for tinted/hover surfaces; saved preset
    // ids and the rest of the legacy catalog remain compatible.
    editorial_intelligence: {
      light: palette(
        "#176C5D", "#FFFFFF", "#D7EEEA", "#103B3A", "#4F615C", "#FFFFFF",
        "#705620", "#FAF8F2", "#1C2B28", "#FFFEFA", "#1C2B28", "#EAEFEC",
        "#30433C", "#63756C", "#C8D3CC", "#B3261E", "#FFFFFF", "#166534",
      ),
      dark: palette(
        "#82D7CA", "#003B38", "#174E4B", "#C6F3EC", "#C3CEC7", "#2A3C36",
        "#E5C785", "#181C1A", "#EDF4EF", "#222825", "#EDF4EF", "#273631",
        "#C3CEC7", "#99ABA0", "#43584C", "#FFB4AB", "#690005", "#86EFAC",
      ),
    },
    material_you_dynamic: {
      light: palette(
        "#304A86",
        "#FFFFFF",
        "#E8EDF9",
        "#171822",
        "#6D5A7E",
        "#FFFFFF",
        "#16A34A",
        "#F7F6FB",
        "#171822",
        "#FFFFFF",
        "#171822",
        "#EEF0F8",
        "#4D4F5D",
        "#737687",
        "#DDE1ED",
      ),
      dark: palette(
        "#91B4FF",
        "#071022",
        "#1B2234",
        "#F8FAFC",
        "#D2B8DF",
        "#111625",
        "#34D399",
        "#080A12",
        "#F8FAFC",
        "#111625",
        "#F8FAFC",
        "#1B2234",
        "#C6CAD5",
        "#9BA3B4",
        "#313A50",
      ),
    },
    premium_amoled: {
      light: palette(
        "#0891B2",
        "#FFFFFF",
        "#CFFAFE",
        "#164E63",
        "#7C3AED",
        "#FFFFFF",
        undefined,
        "#F8FAFC",
        "#0F172A",
        "#FFFFFF",
        "#0F172A",
        "#F1F5F9",
        "#475569",
        "#E2E8F0",
        "#E2E8F0",
        "#F87171",
        "#000000",
      ),
      dark: palette(
        "#22D3EE",
        "#000000",
        "#164E63",
        "#CFFAFE",
        "#A78BFA",
        "#000000",
        "#34D399",
        "#000000",
        "#F8FAFC",
        "#030303",
        "#F8FAFC",
        "#18181B",
        "#A1A1AA",
        "#3F3F46",
        "#27272A",
        "#F87171",
        "#000000",
      ),
    },
    glassmorphism: {
      light: palette(
        "#2563EB",
        "#FFFFFF",
        "#EFF6FF",
        "#1E3A8A",
        "#6366F1",
        "#FFFFFF",
        undefined,
        "#F0F9FF",
        "#0F172A",
        "rgba(255,255,255,0.85)",
        "#0F172A",
        "rgba(224,242,254,0.75)",
        "#475569",
        "rgba(203,213,225,0.8)",
        "rgba(203,213,225,0.8)",
      ),
      dark: palette(
        "#93C5FD",
        "#0B1220",
        "rgba(30,41,59,0.85)",
        "#E2E8F0",
        "#C4B5FD",
        "#0F172A",
        undefined,
        "#0B1220",
        "#F1F5F9",
        "rgba(30,41,59,0.72)",
        "#F8FAFC",
        "rgba(51,65,85,0.65)",
        "#CBD5E1",
        "rgba(100,116,139,0.5)",
        "rgba(71,85,105,0.35)",
      ),
    },
    minimal_white_luxury: {
      light: palette(
        "#18181B",
        "#FFFFFF",
        "#F4F4F5",
        "#18181B",
        "#71717A",
        "#FFFFFF",
        undefined,
        "#FFFFFF",
        "#18181B",
        "#FFFFFF",
        "#18181B",
        "#F4F4F5",
        "#52525B",
        "#E4E4E7",
        "#E4E4E7",
      ),
      dark: palette(
        "#FAFAFA",
        "#18181B",
        "#27272A",
        "#FAFAFA",
        "#A1A1AA",
        "#18181B",
        undefined,
        "#09090B",
        "#FAFAFA",
        "#18181B",
        "#FAFAFA",
        "#27272A",
        "#D4D4D8",
        "#52525B",
        "#52525B",
      ),
    },
    cyberpunk_neon: {
      light: palette(
        "#A855F7",
        "#FFFFFF",
        "#F3E8FF",
        "#581C87",
        "#06B6D4",
        "#FFFFFF",
        undefined,
        "#FFF7FB",
        "#4C1D95",
        "#FFFFFF",
        "#4C1D95",
        "#FCE7F3",
        "#86198F",
        "#E9D5FF",
        "#E9D5FF",
      ),
      dark: palette(
        "#E879F9",
        "#1A0136",
        "#581C87",
        "#FAE8FF",
        "#22D3EE",
        "#083344",
        "#FB7185",
        "#0D0221",
        "#FDF4FF",
        "#1A0B2E",
        "#FDF4FF",
        "#2E1065",
        "#E9D5FF",
        "rgba(192,132,252,0.45)",
        "rgba(192,132,252,0.45)",
      ),
    },
    nature: {
      light: palette(
        "#15803D",
        "#FFFFFF",
        "#DCFCE7",
        "#14532D",
        "#CA8A04",
        "#FFFFFF",
        undefined,
        "#F8FAF8",
        "#14532D",
        "#FFFFFF",
        "#14532D",
        "#ECFDF5",
        "#166534",
        "#BBF7D0",
        "#BBF7D0",
      ),
      dark: palette(
        "#4ADE80",
        "#052E16",
        "#166534",
        "#DCFCE7",
        "#FBBF24",
        "#422006",
        undefined,
        "#052E16",
        "#ECFDF5",
        "#14532D",
        "#ECFDF5",
        "#166534",
        "#D9F99D",
        "rgba(34,197,94,0.35)",
        "rgba(34,197,94,0.35)",
      ),
    },
    midnight_blue_pro: {
      light: palette(
        "#0369A1",
        "#FFFFFF",
        "#E0F2FE",
        "#0C4A6E",
        "#475569",
        "#FFFFFF",
        undefined,
        "#F8FAFC",
        "#0F172A",
        "#FFFFFF",
        "#0F172A",
        "#F1F5F9",
        "#475569",
        "#CBD5E1",
        "#CBD5E1",
      ),
      dark: palette(
        "#38BDF8",
        "#082F49",
        "#0C4A6E",
        "#E0F2FE",
        "#94A3B8",
        "#0F172A",
        undefined,
        "#020617",
        "#F8FAFC",
        "#0F172A",
        "#F8FAFC",
        "#1E293B",
        "#CBD5E1",
        "#334155",
        "#334155",
      ),
    },
    sunset_gradient: {
      light: palette(
        "#EA580C",
        "#FFFFFF",
        "#FFEDD5",
        "#9A3412",
        "#DB2777",
        "#FFFFFF",
        undefined,
        "#FFF7ED",
        "#431407",
        "#FFFFFF",
        "#431407",
        "#FFEDD5",
        "#9A3412",
        "#FDBA74",
        "#FDBA74",
      ),
      dark: palette(
        "#FB923C",
        "#431407",
        "#9A3412",
        "#FFF7ED",
        "#F472B6",
        "#500724",
        undefined,
        "#1C0A05",
        "#FFF7ED",
        "#431407",
        "#FFF7ED",
        "#7C2D12",
        "#FED7AA",
        "rgba(234,88,12,0.4)",
        "rgba(234,88,12,0.4)",
      ),
    },
    accessibility_high_contrast: {
      light: palette(
        "#000000",
        "#FFFFFF",
        "#E5E5E5",
        "#000000",
        "#1D4ED8",
        "#FFFFFF",
        undefined,
        "#FFFFFF",
        "#000000",
        "#FFFFFF",
        "#000000",
        "#F5F5F5",
        "#171717",
        "#000000",
        "#737373",
        "#B91C1C",
        "#FFFFFF",
      ),
      dark: palette(
        "#FACC15",
        "#000000",
        "#422006",
        "#FEF9C3",
        "#38BDF8",
        "#000000",
        undefined,
        "#000000",
        "#FFFFFF",
        "#0A0A0A",
        "#FFFFFF",
        "#171717",
        "#E5E5E5",
        "#FFFFFF",
        "#A3A3A3",
        "#FF6B6B",
        "#000000",
      ),
    },
    minimal_monochrome: {
      light: palette(
        "#262626",
        "#FAFAFA",
        "#E5E5E5",
        "#171717",
        "#737373",
        "#FFFFFF",
        undefined,
        "#FAFAFA",
        "#171717",
        "#FFFFFF",
        "#171717",
        "#F5F5F5",
        "#525252",
        "#D4D4D4",
        "#D4D4D4",
      ),
      dark: palette(
        "#E5E5E5",
        "#171717",
        "#404040",
        "#FAFAFA",
        "#A3A3A3",
        "#171717",
        undefined,
        "#0A0A0A",
        "#FAFAFA",
        "#171717",
        "#FAFAFA",
        "#262626",
        "#D4D4D4",
        "#525252",
        "#525252",
      ),
    },
    festival_burst: {
      light: palette(
        "#A16207",
        "#FFFFFF",
        "#FEF3C7",
        "#78350F",
        "#DC2626",
        "#FFFFFF",
        "#7C3AED",
        "#FFFBEB",
        "#451A03",
        "#FFFFFF",
        "#451A03",
        "#FEF3C7",
        "#92400E",
        "#FCD34D",
        "#FDE68A",
      ),
      dark: palette(
        "#FBBF24",
        "#451A03",
        "#92400E",
        "#FEF3C7",
        "#F87171",
        "#450A0A",
        "#A78BFA",
        "#1C0A05",
        "#FEF3C7",
        "#451A03",
        "#FEF3C7",
        "#78350F",
        "#FDE68A",
        "#8B5B17",
        "#65410F",
      ),
    },
  };

const THEME_PALETTE_ID_SET = new Set<string>(THEME_PALETTE_IDS);

export const THEME_PALETTES: ThemePalette[] = THEME_PALETTE_IDS.map((id) => {
  const meta = PALETTE_META[id];
  const material = MATERIAL_PALETTES[id];
  return {
    id,
    label: meta.label,
    description: meta.description,
    category: meta.category,
    premium: meta.premium,
    light: materialToThemeTokens(material.light),
    dark: materialToThemeTokens(material.dark),
  };
});

export function isThemePaletteId(value: unknown): value is ThemePaletteId {
  return typeof value === "string" && THEME_PALETTE_ID_SET.has(value);
}

export function normalizeThemePaletteId(value: unknown): ThemePaletteId {
  if (isThemePaletteId(value)) return value;
  if (typeof value === "string") {
    const legacy = LEGACY_WEB_THEME_TO_PALETTE[value.trim()];
    if (legacy) return legacy;
  }
  return DEFAULT_THEME_PALETTE_ID;
}

export function getThemePalette(id: unknown): ThemePalette {
  const normalized = normalizeThemePaletteId(id);
  return THEME_PALETTES.find((paletteEntry) => paletteEntry.id === normalized) || THEME_PALETTES[0];
}

export function resolveThemeTokens(
  id: unknown,
  mode: "light" | "dark",
  highContrast = false,
): ThemeTokenSet {
  const tokens = getThemePalette(id)[mode];
  if (!highContrast) return tokens;
  const maximumForeground = mode === "light" ? "#000000" : "#FFFFFF";
  const maximumAccentForeground = maximumContrastColor(tokens.cssVars["--tb-accent"]);
  return {
    ...tokens,
    cssVars: {
      ...tokens.cssVars,
      "--tb-fg-primary": maximumForeground,
      "--tb-fg-secondary": maximumForeground,
      "--tb-fg-muted": maximumForeground,
      "--tb-fg-faint": maximumForeground,
      "--tb-accent-contrast": maximumAccentForeground,
      "--tb-border": maximumForeground,
      "--tb-border-strong": maximumForeground,
    },
  };
}

function materialToThemeTokens(pal: MaterialPalette): ThemeTokenSet {
  const lightBg = luminance(pal.background) > 0.55;
  const contentSurfaces = [
    pal.background,
    resolveOpaqueSurface(pal.surface, pal.background),
    resolveOpaqueSurface(pal.surfaceVariant, pal.background),
  ];
  const foreground = ensureTextContrast(pal.onBackground, contentSurfaces);
  const secondaryForeground = ensureTextContrast(pal.onSurfaceVariant, contentSurfaces);
  // Several expressive palettes intentionally use translucent Material outline
  // seeds. Text/control contrast must be derived from the color users actually
  // see over the palette canvas, then exposed as an opaque stable token.
  const opaqueOutline = resolveOpaqueSurface(pal.outline, pal.background);
  const faintForeground = ensureTextContrast(opaqueOutline, contentSurfaces);
  const strongBorder = ensureTextContrast(opaqueOutline, contentSurfaces, false, 3);
  const accent = ensureTextContrast(pal.primary, contentSurfaces, true);
  const accentSoftSurfaces = contentSurfaces.map((surface) => tintSurface(accent, surface));
  const accentHover = ensureTextContrast(
    pal.secondary,
    [...contentSurfaces, ...accentSoftSurfaces],
    true,
  );
  const danger = ensureTextContrast(
    pal.error || (lightBg ? "#B91C1C" : "#FCA5A5"),
    contentSurfaces,
    true,
  );
  const success = ensureTextContrast(
    pal.success || pal.tertiary || (lightBg ? "#166534" : "#86EFAC"),
    contentSurfaces,
    true,
  );
  const premium = ensureTextContrast(pal.tertiary || pal.secondary, contentSurfaces, true);
  const tertiary = ensureTextContrast(pal.tertiary || pal.secondary, contentSurfaces, true);
  const metaThemeColor = pal.background.startsWith("#") ? pal.background : pal.surface;
  const cssVars = {
    "--tb-bg-canvas": pal.background,
    "--tb-bg-elevated": pal.surface,
    "--tb-bg-subtle": pal.surfaceVariant,
    "--tb-bg-soft": pal.surfaceVariant,
    "--tb-fg-primary": foreground,
    "--tb-fg-secondary": secondaryForeground,
    // Muted copy is still content and must retain readable contrast. Outline
    // seeds are adjusted before they are exposed as faint text or a control
    // boundary because several Material palettes intentionally ship them below
    // WCAG text/non-text contrast.
    "--tb-fg-muted": secondaryForeground,
    "--tb-fg-faint": faintForeground,
    "--tb-accent": accent,
    "--tb-accent-hover": accentHover,
    "--tb-accent-contrast": preferredContrastColor(pal.onPrimary, [accent, accentHover]),
    "--tb-tertiary": tertiary,
    "--tb-premium": premium,
    "--tb-border": pal.outlineVariant || pal.outline,
    "--tb-border-strong": strongBorder,
    "--tb-danger": danger,
    "--tb-danger-contrast": preferredContrastColor(pal.onError, [danger]),
    "--tb-success": success,
    "--tb-accent-soft": alpha(accent, 0.14),
  } satisfies Record<ThemeCssVarName, string>;

  return {
    cssVars,
    metaThemeColor,
    swatches: [pal.primary, pal.secondary, pal.surfaceVariant, pal.background],
  };
}

function palette(
  primary: string,
  onPrimary: string,
  primaryContainer: string,
  onPrimaryContainer: string,
  secondary: string,
  onSecondary: string,
  tertiary: string | undefined,
  background: string,
  onBackground: string,
  surface: string,
  onSurface: string,
  surfaceVariant: string,
  onSurfaceVariant: string,
  outline: string,
  outlineVariant: string,
  error?: string,
  onError?: string,
  success?: string,
): MaterialPalette {
  return {
    primary,
    onPrimary,
    primaryContainer,
    onPrimaryContainer,
    secondary,
    onSecondary,
    tertiary,
    background,
    onBackground,
    surface,
    onSurface,
    surfaceVariant,
    onSurfaceVariant,
    outline,
    outlineVariant,
    error,
    onError,
    success,
  };
}

function alpha(color: string, amount: number): string {
  if (!color.startsWith("#")) return color;
  const h = color.replace("#", "").slice(0, 6);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${amount})`;
}

function luminance(color: string): number {
  const rgb = parseHexColor(color);
  if (!rgb) return 0.5;
  const channels = [rgb.r, rgb.g, rgb.b].map((value) => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function maximumContrastColor(background: string): "#000000" | "#FFFFFF" {
  return maximumSharedContrastColor([background]);
}

function preferredContrastColor(preferred: string | undefined, backgrounds: string[]): string {
  return preferred && minimumContrast(preferred, backgrounds) >= 4.5
    ? preferred
    : maximumSharedContrastColor(backgrounds);
}

type RgbColor = { r: number; g: number; b: number };

function parseHexColor(color: string): RgbColor | null {
  const match = /^#([0-9a-f]{6})$/i.exec(color.trim());
  if (!match) return null;
  return {
    r: Number.parseInt(match[1].slice(0, 2), 16),
    g: Number.parseInt(match[1].slice(2, 4), 16),
    b: Number.parseInt(match[1].slice(4, 6), 16),
  };
}

function toHexColor({ r, g, b }: RgbColor): string {
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

function mixHexColor(color: string, target: "#000000" | "#FFFFFF", amount: number): string {
  const sourceRgb = parseHexColor(color);
  const targetRgb = parseHexColor(target);
  if (!sourceRgb || !targetRgb) return color;
  return toHexColor({
    r: sourceRgb.r + (targetRgb.r - sourceRgb.r) * amount,
    g: sourceRgb.g + (targetRgb.g - sourceRgb.g) * amount,
    b: sourceRgb.b + (targetRgb.b - sourceRgb.b) * amount,
  });
}

function contrastRatio(foreground: string, background: string): number {
  const lighter = Math.max(luminance(foreground), luminance(background));
  const darker = Math.min(luminance(foreground), luminance(background));
  return (lighter + 0.05) / (darker + 0.05);
}

function tintSurface(color: string, surface: string, amount = 0.14): string {
  const colorRgb = parseHexColor(color);
  const surfaceRgb = parseHexColor(surface);
  if (!colorRgb || !surfaceRgb) return surface;
  return toHexColor({
    r: colorRgb.r * amount + surfaceRgb.r * (1 - amount),
    g: colorRgb.g * amount + surfaceRgb.g * (1 - amount),
    b: colorRgb.b * amount + surfaceRgb.b * (1 - amount),
  });
}

function resolveOpaqueSurface(color: string, background: string): string {
  if (parseHexColor(color)) return color;
  const rgbaMatch = /^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/i.exec(
    color.trim(),
  );
  const backgroundRgb = parseHexColor(background);
  if (!rgbaMatch || !backgroundRgb) return background;
  const amount = Math.max(0, Math.min(1, Number(rgbaMatch[4])));
  return toHexColor({
    r: Number(rgbaMatch[1]) * amount + backgroundRgb.r * (1 - amount),
    g: Number(rgbaMatch[2]) * amount + backgroundRgb.g * (1 - amount),
    b: Number(rgbaMatch[3]) * amount + backgroundRgb.b * (1 - amount),
  });
}

function minimumContrast(color: string, surfaces: string[], includeTintedSurfaces = false): number {
  const backgrounds = includeTintedSurfaces
    ? [...surfaces, ...surfaces.map((surface) => tintSurface(color, surface))]
    : surfaces;
  return Math.min(...backgrounds.map((surface) => contrastRatio(color, surface)));
}

function ensureTextContrast(
  color: string,
  surfaces: string[],
  includeTintedSurfaces = false,
  targetRatio = 4.5,
): string {
  if (!parseHexColor(color) || surfaces.some((surface) => !parseHexColor(surface))) return color;
  if (minimumContrast(color, surfaces, includeTintedSurfaces) >= targetRatio) return color;

  let best:
    | {
        color: string;
        amount: number;
        contrast: number;
      }
    | undefined;
  for (const target of ["#000000", "#FFFFFF"] as const) {
    for (let step = 1; step <= 100; step += 1) {
      const candidate = mixHexColor(color, target, step / 100);
      const contrast = minimumContrast(candidate, surfaces, includeTintedSurfaces);
      if (contrast < targetRatio) continue;
      if (
        !best ||
        step / 100 < best.amount ||
        (step / 100 === best.amount && contrast > best.contrast)
      ) {
        best = { color: candidate, amount: step / 100, contrast };
      }
      break;
    }
  }
  return best?.color || maximumSharedContrastColor(surfaces);
}

function maximumSharedContrastColor(backgrounds: string[]): "#000000" | "#FFFFFF" {
  const blackContrast = Math.min(
    ...backgrounds.map((background) => contrastRatio("#000000", background)),
  );
  const whiteContrast = Math.min(
    ...backgrounds.map((background) => contrastRatio("#FFFFFF", background)),
  );
  return blackContrast >= whiteContrast ? "#000000" : "#FFFFFF";
}
